import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskLine, Reveal, Wordmark } from "../components/Motion";
import { ProductCard } from "../components/ProductCard";
import { EmailSignup } from "../components/EmailSignup";
import { BundleSection } from "../components/BundleSection";
import { DonationSection } from "../components/DonationSection";
import { getProducts } from "../lib/api";

const Marquee = ({ text, className = "" }) => (
  <div className={`overflow-hidden border-y border-line py-4 ${className}`}>
    <div className="marquee-track font-display font-extrabold text-4xl sm:text-6xl uppercase tracking-tight">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="mx-6 text-cream/90">{text} <span className="text-cs-yellow">✳</span></span>
      ))}
    </div>
  </div>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => { getProducts().then(setProducts).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[92vh] min-h-[600px] overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src="/brand/graffiti.png" alt="Covertswag campaign" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/30 to-bg/40" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative h-full max-w-[1500px] mx-auto px-4 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-cs-yellow mb-6">
            <MaskLine>First Drop · Fall 2026</MaskLine>
          </div>
          <h1 className="font-display font-extrabold text-[15vw] sm:text-[11vw] lg:text-[9rem] leading-[0.82] uppercase">
            <MaskLine delay={0.05}>Already</MaskLine>
            <MaskLine delay={0.15} className="text-graytx">Lived</MaskLine>
            <MaskLine delay={0.25}>In</MaskLine>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-3 mt-10">
            <Link to="/shop" data-testid="hero-shop-btn"
              className="inline-flex items-center justify-center gap-2 bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] px-8 py-4 hover:bg-cs-yellow transition-colors duration-200">
              Shop the Drop <ArrowRight size={15} />
            </Link>
            <Link to="/lookbook" data-testid="hero-lookbook-btn"
              className="inline-flex items-center justify-center gap-2 border border-cream/40 font-mono text-[12px] uppercase tracking-[0.2em] px-8 py-4 hover:border-cream hover:bg-cream/5 transition-colors duration-200">
              View Lookbook <ArrowUpRight size={15} />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee text="Pre-orders now open" />

      {/* FIRST DROP */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-8 py-24 sm:py-32" data-testid="first-drop-section">
        <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-14">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-graytx">01 — The Drop</p>
            <h2 className="font-display font-extrabold text-5xl sm:text-7xl uppercase leading-[0.9] mt-4">First Drop</h2>
          </div>
          <p className="text-graytx max-w-sm text-sm leading-relaxed">
            Two pieces. Washed black denim, distressed layered cotton, raised multicolor stitchwork. Limited first run — hover to flip.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 sm:gap-10">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      <BundleSection />

      {/* LOOKBOOK TEASER — asymmetric */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="lookbook-teaser">
        <div className="grid grid-cols-12 gap-4 sm:gap-6 items-end">
          <Reveal className="col-span-7 sm:col-span-5 aspect-[3/4] overflow-hidden">
            <img src="/brand/jorts_back.png" alt="Boardwalk" className="w-full h-full object-cover" />
          </Reveal>
          <Reveal delay={0.1} className="col-span-5 sm:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-graytx">02 — Lookbook</p>
            <p className="font-editorial italic text-2xl sm:text-4xl leading-tight mt-4">
              Shot on the boardwalk, the stairwell, and the concrete underground.
            </p>
            <Link to="/lookbook" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] mt-6 hover:text-cs-yellow transition-colors duration-200" data-testid="lookbook-teaser-link">
              See the full editorial <ArrowRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={0.2} className="col-span-12 sm:col-span-3 aspect-[3/4] overflow-hidden hidden sm:block">
            <img src="/brand/stairwell.png" alt="Stairwell" className="w-full h-full object-cover" />
          </Reveal>
        </div>
      </section>

      <Marquee text="Sun-faded · Thrift-built · Underground" className="marquee-slow" />

      {/* OUR STORY teaser */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-24 sm:py-32 text-center" data-testid="story-teaser">
        <Reveal>
          <Wordmark className="text-3xl" />
          <p className="font-editorial text-2xl sm:text-4xl lg:text-5xl leading-[1.25] mt-8">
            We blend sun-faded surf culture, vintage thrift finds, and washed fabrics into modern streetwear that feels like it's always been yours.
          </p>
          <Link to="/our-story" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] mt-10 hover:text-cs-yellow transition-colors duration-200" data-testid="story-teaser-link">
            Read our story <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>

      {/* MISSION teaser */}
      <section className="relative overflow-hidden" data-testid="mission-teaser">
        <img src="/brand/duo.png" alt="Mission" className="w-full h-[70vh] object-cover" />
        <div className="absolute inset-0 bg-bg/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1500px] mx-auto px-4 sm:px-8 w-full">
            <Reveal className="max-w-xl">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cs-green">The Mission</p>
              <h2 className="font-display font-extrabold text-4xl sm:text-6xl uppercase leading-[0.9] mt-5">
                8% fights<br />the cold
              </h2>
              <p className="text-cream/80 mt-6 leading-relaxed">
                Every order gives 8% to organizations clothing people in poverty who have nothing to warm up in. Style that keeps someone else warm.
              </p>
              <Link to="/mission" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] mt-8 border border-cream/40 px-6 py-3 hover:bg-cream hover:text-bg transition-colors duration-200" data-testid="mission-teaser-link">
                Read the mission <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <EmailSignup />

      <DonationSection />
    </div>
  );
}
